import { useGetMyBookingByRefQuery } from '@massilia/queries'
import { Name } from '@massilia/ui'
import { Link } from '@tanstack/react-router'
import { ChevronLeft, Loader2 } from 'lucide-react'
import { useMemo, useState } from 'react'

import { CarRentalsSection } from '@/components/booking-sections/car-rentals-section'
import { ExtrasSection } from '@/components/booking-sections/extras-section'
import { HotelsSection } from '@/components/booking-sections/hotels-section'
import { InsuranceSection } from '@/components/booking-sections/insurance-section'
import { InstallmentTimeline } from '@/components/booking/installment-timeline'
import { CompleteYourTripSection } from '@/components/payment-confirmation/complete-your-trip-section'
import { PaymentConfirmationSharedScreen } from '@/components/payment-confirmation/payment-confirmation-shared-screen'
import { TripRecapSection } from '@/components/payment-confirmation/trip-recap-section'
import { SpecificitySection } from '@/components/specificity/specificity-section'
import { useAuthStore } from '@/stores/auth.store'

import { BookingBottomActions } from './booking-bottom-actions'
import { BookingContactModal, type BookingContactModalVariant } from './booking-contact-modal'
import { BookingSummaryCard } from './booking-summary-card'
import { buildBookingGreeting } from './build-booking-greeting'
import { mapBookingDetailToConfirmationSummary } from './map-booking-to-trip-summary'
import { PaymentSettlementSection } from './payment-settlement-section'
import { TicketDeliveryNotice } from './ticket-delivery-notice'

interface BookingDetailPageProps {
  bookingRef: string
}

export function BookingDetailPage({ bookingRef }: BookingDetailPageProps) {
  const { user } = useAuthStore()
  const { data: booking, error, isLoading } = useGetMyBookingByRefQuery(bookingRef)
  const [modalVariant, setModalVariant] = useState<BookingContactModalVariant>(null)

  const summary = useMemo(
    () => (booking ? mapBookingDetailToConfirmationSummary(booking) : undefined),
    [booking]
  )

  const showPaymentSection =
    booking?.status === 'DEPOSIT_PAID' || booking?.status === 'AWAITING_DEPOSIT'

  const isCancelled = booking?.status === 'CANCELLED'
  const hasActiveInsurance = !!booking?.insurance && !!booking?.insuranceActivatedAt

  const greeting = buildBookingGreeting(booking?.client, user)

  return (
    <div className="flex min-h-screen flex-col bg-white">
      {/* Content */}
      {/* `px-4` sur téléphone : à 24 px de marge de page plus 24 px de marge de
          carte, il ne restait qu'une poignée de caractères par ligne. */}
      <main className="mx-auto flex w-full max-w-268 flex-col gap-4 px-4 py-6 md:gap-6 md:px-6 md:py-10">
        {/* Breadcrumb */}
        <Link className="flex items-center gap-1 text-sm font-medium text-brand-600" to="/">
          <ChevronLeft className="size-4" />
          Retour aux réservations
        </Link>

        {/* Greeting */}
        {user && (
          <div className="flex flex-col gap-2">
            {/* Le `text-5xl` d'origine mangeait deux écrans de haut sur
                téléphone avant le moindre contenu utile. */}
            <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">
              Bienvenue{greeting.title ? ` ${greeting.title}` : ''} <Name>{greeting.name}</Name>
            </h1>
            {booking && (
              <p className="text-sm font-medium break-words text-gray-light-700">
                Numéro de dossier Massilia : <b>{booking.pnr ?? booking.reference}</b>
              </p>
            )}
          </div>
        )}

        {/* Loading state */}
        {isLoading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="size-8 animate-spin text-brand-600" />
          </div>
        )}

        {/* Error state */}
        {error && !isLoading && (
          <div className="flex flex-col items-center gap-4 rounded-2xl border border-gray-light-200 bg-white p-6 md:p-10">
            <p className="text-sm text-gray-light-700">
              Impossible de charger les détails de la réservation.
            </p>
            <Link className="text-sm font-medium text-brand-600 underline" to="/">
              Retour aux réservations
            </Link>
          </div>
        )}

        {/* Cancelled state */}
        {booking && isCancelled && (
          <div className="flex flex-col items-center gap-4 rounded-2xl border border-gray-light-200 bg-white p-6 md:p-10">
            <p className="text-lg font-semibold text-gray-light-900">Ce dossier a été annulé</p>
            <p className="text-sm text-gray-light-700">
              Pour toute question, veuillez contacter notre agence.
            </p>
            <Link className="text-sm font-medium text-brand-600 underline" to="/">
              Retour aux réservations
            </Link>
          </div>
        )}

        {/* Main booking content */}
        {booking && !isCancelled && (
          <>
            {showPaymentSection && booking.installmentChosen && booking.paymentDeadline && (
              <InstallmentTimeline
                balanceDeadline={booking.paymentDeadline}
                departureDate={booking.departureDate}
                {...(booking.status === 'AWAITING_DEPOSIT' &&
                  booking.depositDeadline &&
                  booking.installmentMinimumDepositAmount !== null &&
                  booking.installmentMinimumDepositAmount !== undefined && {
                    depositDeadline: booking.depositDeadline,
                    minimumDepositCents: booking.installmentMinimumDepositAmount,
                  })}
              />
            )}

            {booking.status === 'FULLY_PAID_PENDING' && <TicketDeliveryNotice />}

            <BookingSummaryCard booking={booking} />

            {showPaymentSection && <PaymentSettlementSection booking={booking} />}

            <TripRecapSection summary={summary} />

            <SpecificitySection bookingId={booking.id} />

            {summary?.insurance && <InsuranceSection insurance={summary.insurance} />}
            {summary?.hotels && summary.hotels.length > 0 && (
              <HotelsSection hotels={summary.hotels} />
            )}
            {summary?.carRentals && summary.carRentals.length > 0 && (
              <CarRentalsSection carRentals={summary.carRentals} />
            )}
            {summary?.extras && summary.extras.length > 0 && (
              <ExtrasSection extras={summary.extras} />
            )}

            <BookingBottomActions
              onCancel={() => setModalVariant('annulation')}
              onModify={() => setModalVariant('modification')}
            />

            <CompleteYourTripSection
              bookingId={booking.id}
              hasInsurance={hasActiveInsurance}
              summary={summary}
            />

            <PaymentConfirmationSharedScreen isReunionAgency={booking.agency === 'RUN'} />
          </>
        )}
      </main>

      {/* Modal */}
      <BookingContactModal
        isReunion={booking?.agency === 'RUN'}
        onClose={() => setModalVariant(null)}
        variant={modalVariant}
      />
    </div>
  )
}
